import React from "react";

import Topbar from "../components/topbar";
import { Typography, useMediaQuery, useTheme, Box, Button, Tooltip } from "@mui/material";
import { Share2, Info } from "lucide-react";
import Triquetra from "../components/icons/triquetra";
import { useSettings } from "../providers/settings-provider";
import Dropdown from "../components/ui/dropdown";
import DynamicTable, { Column } from "../components/table";
import { Menu } from "lucide-react";
import { useSidebarMobile } from "../providers/sidebar-mobile-context";
import useApi from "../hooks/use-api";
import buildApiRequestConfig from "../api/url-builder";
import routes from "../api/routes";
import { useUser } from "../providers/user-provider";
import { useFeatures } from "../providers/features-provider";
import {
    MajorHeading, GLCode, flattenCategories, buildMajorHeadingGroups, hexToRgba,
    PeriodType, getDateRange, getBucketId, getBuckets, UNCATEGORIZED_KEY, buildSumsByCategory,
} from "../util/forecast-groups";
import SimulationsPopup from "../components/popup/simulations";
import SimulationCellDetail from "../components/popup/simulation-cell-detail";
import ShareReport from "../components/popup/share-report";
import { ReportSnapshot } from "../util/report-snapshot";
import { openPlanPrompt } from '../util/plan-required';

type ForecastActual = { forecast: number; actual: number };
type TableGroup = { label: string; rows: Record<string, any>[] };

const buildGroupTotalRow = (rows: Record<string, any>[], bucketIds: string[]): Record<string, any> => {
    const total: Record<string, any> = { category: 'TOTAL', isTotal: true };
    bucketIds.forEach(id => {
        total[id] = {
            forecast: rows.reduce((sum, r) => sum + (r[id]?.forecast || 0), 0),
            actual: rows.reduce((sum, r) => sum + (r[id]?.actual || 0), 0),
        };
    });
    // A total is "affected" in a bucket if any row rolled into it was affected there
    // (forecast and/or actual changed by the simulation), even if the net change is zero.
    const affectedBuckets = new Set<string>();
    rows.forEach(r => { (r.affectedBuckets as Set<string> | undefined)?.forEach(id => affectedBuckets.add(id)); });
    total.affectedBuckets = affectedBuckets;
    // Every category rolled into this total, so clicking an affected cell here can
    // show the combined detail across all of them.
    total.categoryKeys = rows.map(r => r.categoryKey).filter(Boolean);
    return total;
};

const BudgetActual: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { setSidebarOpen } = useSidebarMobile();
    const { formatAmount, formatDate } = useSettings();
    const { user } = useUser();
    const { isVisibleInNav } = useFeatures();
    const hasDocumentsAddon = isVisibleInNav('documents');

    const [majorHeadings, setMajorHeadings] = React.useState<MajorHeading[]>([]);
    const [allCodes, setAllCodes] = React.useState<GLCode[]>([]);
    const [period, setPeriod] = React.useState<PeriodType>('month');
    const [provisionTransactions, setProvisionTransactions] = React.useState<any[]>([]);
    const [baselineTransactions, setBaselineTransactions] = React.useState<any[]>([]);
    const [realTransactions, setRealTransactions] = React.useState<any[]>([]);
    const [realBaselineTransactions, setRealBaselineTransactions] = React.useState<any[]>([]);
    const [fetchGroups, setFetchGroups] = useApi();
    const [fetchCategories, setFetchCategories] = useApi();
    const [fetchProvisions, setFetchProvisions] = useApi();
    const [fetchBaseline, setFetchBaseline] = useApi();
    const [fetchActuals, setFetchActuals] = useApi();
    const [fetchActualsBaseline, setFetchActualsBaseline] = useApi();
    // Sub Headings default to collapsed (empty set = nothing expanded) so the
    // page doesn't take up much space until the user opens what they need.
    const [expandedSubHeadings, setExpandedSubHeadings] = React.useState<Set<number>>(new Set());
    const toggleSubHeading = React.useCallback((subHeadingId: number) => {
        setExpandedSubHeadings(prev => {
            const next = new Set(prev);
            if (next.has(subHeadingId)) next.delete(subHeadingId);
            else next.add(subHeadingId);
            return next;
        });
    }, []);
    // Major Headings default to expanded (empty set = nothing collapsed) -
    // opposite default from Sub Headings, matching the target layout where
    // headings are open by default and only "Other" starts collapsed.
    const [collapsedMajorHeadings, setCollapsedMajorHeadings] = React.useState<Set<number>>(new Set());
    const toggleMajorHeading = React.useCallback((majorHeadingId: number) => {
        setCollapsedMajorHeadings(prev => {
            const next = new Set(prev);
            if (next.has(majorHeadingId)) next.delete(majorHeadingId);
            else next.add(majorHeadingId);
            return next;
        });
    }, []);
    const [otherExpanded, setOtherExpanded] = React.useState(false);
    // Simulations can target either side - a rule can exclude/move/defer provisions
    // (Forecast) or exclude real transactions (Actual), so both fetches apply it.
    const [appliedSimulation, setAppliedSimulation] = React.useState<{ id: string; name: string; simulationDetails?: any[] } | null>(null);
    const [simulationsPopupOpen, setSimulationsPopupOpen] = React.useState(false);
    // undefined = no category scoping (opened from the header button); null = uncategorized; number = one category
    const [simulationsPopupCategoryId, setSimulationsPopupCategoryId] = React.useState<number | null | undefined>(undefined);
    // categoryKeys has one entry for a single GL code's cell, or many for a TOTAL/Subtotal cell
    const [cellDetailTarget, setCellDetailTarget] = React.useState<{ categoryKeys: string[]; categoryLabel: string; bucketId: string; bucketLabel: string } | null>(null);
    const [shareOpen, setShareOpen] = React.useState(false);

    React.useEffect(() => {
        document.title = "FineAnts | Forecast vs Actual";
    }, []);

    // Learned from the provisions fetch's response (status.endOfHorizon) -
    // null until the first response lands, at which point the range below
    // extends to meet it if it reaches further than the default padding.
    const [horizonDate, setHorizonDate] = React.useState<Date | null>(null);
    const dateRange = React.useMemo(() => getDateRange(period, new Date(), horizonDate), [period, horizonDate]);
    const buckets = React.useMemo(() => getBuckets(period, dateRange.start, dateRange.end), [period, dateRange]);
    const bucketIds = React.useMemo(() => buckets.map(b => b.id), [buckets]);

    React.useEffect(() => {
        if (!user?.entityId) return;
        const groupsReq = buildApiRequestConfig(routes.forecastGroups.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
        setFetchGroups(groupsReq);
        const catsReq = buildApiRequestConfig(routes.categories.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
        setFetchCategories(catsReq);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.entityId]);

    // Forecast is the sum of transactions of type "provision" within the coverage window
    React.useEffect(() => {
        if (!user?.entityId) return;
        const config = buildApiRequestConfig(routes.transaction.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
        setFetchProvisions({
            ...config,
            body: {
                startDate: dateRange.start.toISOString(),
                endDate: dateRange.end.toISOString(),
                showReal: false,
                showRecurring: true,
                showOneTime: true,
                pageSize: 99999,
                simulationId: appliedSimulation?.id || undefined,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.entityId, dateRange, appliedSimulation]);

    // Actual is the sum of transactions of type "real" - a simulation can exclude
    // real transactions too, so this also applies the currently applied simulation.
    React.useEffect(() => {
        if (!user?.entityId) return;
        const config = buildApiRequestConfig(routes.transaction.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
        setFetchActuals({
            ...config,
            body: {
                startDate: dateRange.start.toISOString(),
                endDate: dateRange.end.toISOString(),
                showReal: true,
                showRecurring: false,
                showOneTime: false,
                pageSize: 99999,
                simulationId: appliedSimulation?.id || undefined,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.entityId, dateRange, appliedSimulation]);

    // When a simulation is applied, also fetch the un-simulated baseline for both sides
    // so we can tell which cells the simulation actually changed (for the red indicator).
    React.useEffect(() => {
        if (!user?.entityId || !appliedSimulation) {
            setBaselineTransactions([]);
            setRealBaselineTransactions([]);
            return;
        }
        const config = buildApiRequestConfig(routes.transaction.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
        setFetchBaseline({
            ...config,
            body: {
                startDate: dateRange.start.toISOString(),
                endDate: dateRange.end.toISOString(),
                showReal: false,
                showRecurring: true,
                showOneTime: true,
                pageSize: 99999,
            },
        });
        setFetchActualsBaseline({
            ...config,
            body: {
                startDate: dateRange.start.toISOString(),
                endDate: dateRange.end.toISOString(),
                showReal: true,
                showRecurring: false,
                showOneTime: false,
                pageSize: 99999,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.entityId, dateRange, appliedSimulation]);

    React.useEffect(() => {
        const { data, isLoading } = fetchGroups;
        if (data && !isLoading) setMajorHeadings(data.majorHeadings || []);
    }, [fetchGroups]);

    React.useEffect(() => {
        const { data, isLoading } = fetchCategories;
        if (data && !isLoading) setAllCodes(flattenCategories(data));
    }, [fetchCategories]);

    React.useEffect(() => {
        const { data, isLoading } = fetchProvisions;
        if (data && !isLoading) {
            setProvisionTransactions(data.transactions ?? []);
            if (data.status?.endOfHorizon) {
                const horizon = new Date(data.status.endOfHorizon);
                setHorizonDate(prev => (!prev || horizon.getTime() !== prev.getTime()) ? horizon : prev);
            }
        }
    }, [fetchProvisions]);

    React.useEffect(() => {
        const { data, isLoading } = fetchActuals;
        if (data && !isLoading) setRealTransactions(data.transactions ?? []);
    }, [fetchActuals]);

    React.useEffect(() => {
        const { data, isLoading } = fetchBaseline;
        if (data && !isLoading) setBaselineTransactions(data.transactions ?? []);
    }, [fetchBaseline]);

    React.useEffect(() => {
        const { data, isLoading } = fetchActualsBaseline;
        if (data && !isLoading) setRealBaselineTransactions(data.transactions ?? []);
    }, [fetchActualsBaseline]);

    const codeName = React.useCallback((categoryId: number) => allCodes.find(c => c.id === categoryId)?.name || `GL Code #${categoryId}`, [allCodes]);

    const sumsByCategory = React.useMemo(() => buildSumsByCategory(provisionTransactions, period), [provisionTransactions, period]);
    const sumsByCategoryBaseline = React.useMemo(() => buildSumsByCategory(baselineTransactions, period), [baselineTransactions, period]);
    const sumsByCategoryActual = React.useMemo(() => buildSumsByCategory(realTransactions, period), [realTransactions, period]);
    const sumsByCategoryActualBaseline = React.useMemo(() => buildSumsByCategory(realBaselineTransactions, period), [realBaselineTransactions, period]);

    const rowValuesForKey = React.useCallback((key: string): Record<string, ForecastActual> => {
        const forecastSums = sumsByCategory[key] || {};
        const actualSums = sumsByCategoryActual[key] || {};
        const row: Record<string, ForecastActual> = {};
        bucketIds.forEach(id => { row[id] = { forecast: forecastSums[id] || 0, actual: actualSums[id] || 0 }; });
        return row;
    }, [sumsByCategory, sumsByCategoryActual, bucketIds]);

    const rowValuesForCategory = React.useCallback((categoryId: number) => rowValuesForKey(String(categoryId)), [rowValuesForKey]);

    // Which of this key's bucket cells did the applied simulation actually change, on
    // either the Forecast side (provisions) or the Actual side (real transactions),
    // compared to the un-simulated baseline - drives the red "affected" indicator.
    const affectedBucketsForKey = React.useCallback((key: string): Set<string> => {
        const affected = new Set<string>();
        if (!appliedSimulation) return affected;
        const simForecast = sumsByCategory[key] || {};
        const baseForecast = sumsByCategoryBaseline[key] || {};
        const simActual = sumsByCategoryActual[key] || {};
        const baseActual = sumsByCategoryActualBaseline[key] || {};
        bucketIds.forEach(id => {
            if ((simForecast[id] || 0) !== (baseForecast[id] || 0) || (simActual[id] || 0) !== (baseActual[id] || 0)) affected.add(id);
        });
        return affected;
    }, [appliedSimulation, sumsByCategory, sumsByCategoryBaseline, sumsByCategoryActual, sumsByCategoryActualBaseline, bucketIds]);

    const affectedBucketsForCategory = React.useCallback((categoryId: number) => affectedBucketsForKey(String(categoryId)), [affectedBucketsForKey]);

    const groups: TableGroup[] = React.useMemo(() => {
        const headingGroups = majorHeadings.map(mh => {
            const g = buildMajorHeadingGroups(mh, codeName);
            const childRows: Record<string, any>[] = [];
            const allLeafRows: Record<string, any>[] = [];

            g.subHeadingGroups.forEach(sh => {
                if (sh.leaves.length === 0) return;
                const isCollapsed = !expandedSubHeadings.has(sh.id);
                const leafRows = sh.leaves.map(l => ({ category: l.name, categoryKey: String(l.categoryId), isNested: true, groupTint: true, groupColor: sh.color, affectedBuckets: affectedBucketsForCategory(l.categoryId), ...rowValuesForCategory(l.categoryId) }));
                // The Sub Heading row itself carries the subtotal (merged in via
                // buildGroupTotalRow) instead of a separate trailing "Subtotal" row.
                childRows.push({ ...buildGroupTotalRow(leafRows, bucketIds), category: sh.name, isGroupLabel: true, isTotal: false, isSubtotal: true, isCollapsed, subHeadingId: sh.id, groupTint: true, groupColor: sh.color });
                if (!isCollapsed) childRows.push(...leafRows);
                allLeafRows.push(...leafRows);
            });

            const directRows = g.directLeaves.map(l => ({ category: l.name, categoryKey: String(l.categoryId), affectedBuckets: affectedBucketsForCategory(l.categoryId), ...rowValuesForCategory(l.categoryId) }));
            childRows.push(...directRows);
            allLeafRows.push(...directRows);

            // Likewise, the Major Heading row itself carries the grand total
            // instead of a separate trailing "TOTAL" row - collapsing it hides
            // everything underneath (Sub Headings and direct GL codes alike).
            const isCollapsed = collapsedMajorHeadings.has(mh.id);
            const headingRow = { ...buildGroupTotalRow(allLeafRows, bucketIds), category: mh.name, isMajorHeading: true, majorHeadingId: mh.id, isCollapsed };
            return { label: mh.name, rows: isCollapsed ? [headingRow] : [headingRow, ...childRows] };
        });

        const assignedIds = new Set<number>();
        majorHeadings.forEach(mh => {
            mh.subGroups.forEach(sg => assignedIds.add(sg.categoryId));
            mh.subHeadings.forEach(sh => sh.subGroups.forEach(sg => assignedIds.add(sg.categoryId)));
        });
        const unassigned = allCodes.filter(c => !assignedIds.has(c.id));

        // "Other" always exists, and "Uncategorized" is always one of its rows -
        // both are always-available buckets to check, not something that only
        // appears once matching data happens to exist.
        const otherRows = unassigned.map(c => ({ category: c.name, categoryKey: String(c.id), affectedBuckets: affectedBucketsForCategory(c.id), ...rowValuesForCategory(c.id) }));
        otherRows.push({ category: 'Uncategorized', categoryKey: UNCATEGORIZED_KEY, affectedBuckets: affectedBucketsForKey(UNCATEGORIZED_KEY), ...rowValuesForKey(UNCATEGORIZED_KEY) });
        const otherIsCollapsed = !otherExpanded;
        const otherHeadingRow = { ...buildGroupTotalRow(otherRows, bucketIds), category: 'OTHER', isMajorHeading: true, isOther: true, isCollapsed: otherIsCollapsed };
        headingGroups.push({ label: 'Other', rows: otherIsCollapsed ? [otherHeadingRow] : [otherHeadingRow, ...otherRows] });

        return headingGroups;
    }, [majorHeadings, allCodes, codeName, expandedSubHeadings, collapsedMajorHeadings, otherExpanded, rowValuesForCategory, rowValuesForKey, affectedBucketsForCategory, affectedBucketsForKey, bucketIds]);

    // Same as `groups`, but always includes every sub-heading's leaf rows
    // regardless of this session's current collapse state - used only for
    // building a share snapshot, so a viewer opening it later can expand
    // anything (the collapse/expand there is handled client-side over this
    // complete dataset, starting collapsed by default, same as this page does).
    const fullyExpandedGroups: TableGroup[] = React.useMemo(() => {
        const headingGroups = majorHeadings.map(mh => {
            const g = buildMajorHeadingGroups(mh, codeName);
            const rows: Record<string, any>[] = [];
            const allLeafRows: Record<string, any>[] = [];

            g.subHeadingGroups.forEach(sh => {
                if (sh.leaves.length === 0) return;
                rows.push({ category: sh.name, isGroupLabel: true, isCollapsed: true, subHeadingId: sh.id, groupTint: true, groupColor: sh.color });
                const leafRows = sh.leaves.map(l => ({ category: l.name, categoryKey: String(l.categoryId), isNested: true, groupTint: true, groupColor: sh.color, affectedBuckets: affectedBucketsForCategory(l.categoryId), ...rowValuesForCategory(l.categoryId) }));
                rows.push(...leafRows);
                rows.push({ ...buildGroupTotalRow(leafRows, bucketIds), category: `${sh.name} Subtotal`, isTotal: false, isSubtotal: true, groupTint: true, groupColor: sh.color });
                allLeafRows.push(...leafRows);
            });

            const directRows = g.directLeaves.map(l => ({ category: l.name, categoryKey: String(l.categoryId), affectedBuckets: affectedBucketsForCategory(l.categoryId), ...rowValuesForCategory(l.categoryId) }));
            rows.push(...directRows);
            allLeafRows.push(...directRows);

            rows.push(buildGroupTotalRow(allLeafRows, bucketIds));
            return { label: mh.name, rows };
        });

        const assignedIds = new Set<number>();
        majorHeadings.forEach(mh => {
            mh.subGroups.forEach(sg => assignedIds.add(sg.categoryId));
            mh.subHeadings.forEach(sh => sh.subGroups.forEach(sg => assignedIds.add(sg.categoryId)));
        });
        const unassigned = allCodes.filter(c => !assignedIds.has(c.id));

        const otherRows = unassigned.map(c => ({ category: c.name, categoryKey: String(c.id), affectedBuckets: affectedBucketsForCategory(c.id), ...rowValuesForCategory(c.id) }));
        otherRows.push({ category: 'Uncategorized', categoryKey: UNCATEGORIZED_KEY, affectedBuckets: affectedBucketsForKey(UNCATEGORIZED_KEY), ...rowValuesForKey(UNCATEGORIZED_KEY) });
        headingGroups.push({ label: 'Other', rows: [...otherRows, buildGroupTotalRow(otherRows, bucketIds)] });

        return headingGroups;
    }, [majorHeadings, allCodes, codeName, rowValuesForCategory, rowValuesForKey, affectedBucketsForCategory, affectedBucketsForKey, bucketIds]);

    const overallTotal = React.useMemo(() => groups.reduce((acc, g) => {
        const totalRow = g.rows.find(r => r.isTotal);
        if (!totalRow) return acc;
        bucketIds.forEach(id => {
            acc.forecast += totalRow[id]?.forecast || 0;
            acc.actual += totalRow[id]?.actual || 0;
        });
        return acc;
    }, { forecast: 0, actual: 0 }), [groups, bucketIds]);

    const otherTotal = React.useMemo(() => {
        const otherGroup = groups.find(g => g.label === 'Other');
        const totalRow = otherGroup?.rows.find(r => r.isTotal);
        if (!totalRow) return { forecast: 0, actual: 0 };
        return bucketIds.reduce((acc, id) => ({
            forecast: acc.forecast + (totalRow[id]?.forecast || 0),
            actual: acc.actual + (totalRow[id]?.actual || 0),
        }), { forecast: 0, actual: 0 });
    }, [groups, bucketIds]);

    const periodLabels: Record<PeriodType, string> = { month: 'by Month', quarter: 'by Quarter', year: 'by Year' };

    const buildSnapshot = React.useCallback((): ReportSnapshot => ({
        reportType: 'forecast-vs-actual',
        periodLabel: periodLabels[period],
        buckets: buckets.map(b => ({ id: b.id, label: b.label })),
        groups: fullyExpandedGroups.map(g => ({
            label: g.label,
            rows: g.rows.map(row => ({
                label: row.category,
                isTotal: !!row.isTotal,
                isSubtotal: !!row.isSubtotal,
                isGroupLabel: !!row.isGroupLabel,
                isNested: !!row.isNested,
                groupColor: row.groupColor,
                cells: row.isGroupLabel ? {} : Object.fromEntries(
                    bucketIds.map(id => [id, {
                        forecast: formatAmount?.(row[id]?.forecast || 0) ?? String(row[id]?.forecast || 0),
                        actual: formatAmount?.(row[id]?.actual || 0) ?? String(row[id]?.actual || 0),
                    }])
                ),
                affectedBucketIds: row.affectedBuckets ? Array.from(row.affectedBuckets as Set<string>) : [],
            })),
        })),
        overallTotal: { forecast: formatAmount?.(overallTotal.forecast) ?? String(overallTotal.forecast), actual: formatAmount?.(overallTotal.actual) ?? String(overallTotal.actual) },
        otherTotal: { forecast: formatAmount?.(otherTotal.forecast) ?? String(otherTotal.forecast), actual: formatAmount?.(otherTotal.actual) ?? String(otherTotal.actual) },
        appliedSimulationName: appliedSimulation?.name || null,
        generatedAt: new Date().toISOString(),
    }), [period, buckets, fullyExpandedGroups, bucketIds, overallTotal, otherTotal, appliedSimulation, formatAmount]);

    // Transactions for the cell currently open in the simulation detail dialog - kept as
    // two separate pairs (Forecast/provisions vs Actual/real) rather than merged, since a
    // simulation rule can change either one and lumping their totals together would mix
    // two different measures in the detail modal.
    const cellDetailTransactions = React.useMemo(() => {
        if (!cellDetailTarget) return { forecastBaseline: [], forecastSimulated: [], actualBaseline: [], actualSimulated: [] };
        const matches = (t: any) => {
            const key = t.categoryId ? String(t.categoryId) : UNCATEGORIZED_KEY;
            return cellDetailTarget.categoryKeys.includes(key) && getBucketId(period, new Date(t.date)) === cellDetailTarget.bucketId;
        };
        return {
            forecastBaseline: baselineTransactions.filter(matches),
            forecastSimulated: provisionTransactions.filter(matches),
            actualBaseline: realBaselineTransactions.filter(matches),
            actualSimulated: realTransactions.filter(matches),
        };
    }, [cellDetailTarget, baselineTransactions, provisionTransactions, realBaselineTransactions, realTransactions, period]);

    // Variance = Actual - Forecast (standard accounting convention: positive
    // means actual came in above forecast, negative means below). Rate is
    // that variance as a percentage of the forecast; with a $0 forecast the
    // percentage is undefined (any nonzero actual is an infinite swing), so
    // that's called out as "N/A" rather than shown as a misleading number.
    const formatVarianceTooltip = (forecast: number, actual: number): { variance: string; rate: string; varianceValue: number } => {
        const variance = (actual || 0) - (forecast || 0);
        const varianceDisplay = `${variance > 0 ? '+' : ''}${formatAmount?.(variance) ?? String(variance)}`;
        const rateDisplay = forecast
            ? `${variance > 0 ? '+' : ''}${((variance / Math.abs(forecast)) * 100).toFixed(1)}%`
            : (variance === 0 ? '0%' : 'N/A');
        return { variance: varianceDisplay, rate: rateDisplay, varianceValue: variance };
    };

    const buildColumns = (): Column[] => [
        {
            id: 'category', label: '', type: 'string', size: 200, headerColor: theme.palette.primary.main,
            // GL codes nested under a Sub Heading are indented one level further to
            // show they belong to it, which itself sits one level under its Major
            // Heading. Major Heading and Sub Heading rows are clickable to
            // collapse/expand what's under them.
            customElement: (value: string, row: Record<string, any>) => (
                <Box
                    onClick={
                        row.isMajorHeading ? (row.isOther ? () => setOtherExpanded(v => !v) : () => toggleMajorHeading(row.majorHeadingId))
                        : row.isGroupLabel ? () => toggleSubHeading(row.subHeadingId)
                        : undefined
                    }
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        pl: row.isMajorHeading ? 1.25 : row.isNested ? 5 : 2.5,
                        cursor: (row.isMajorHeading || row.isGroupLabel) ? 'pointer' : 'default',
                        userSelect: 'none',
                    }}
                >
                    {(row.isMajorHeading || row.isGroupLabel) && (
                        <Typography component="span" sx={{ fontSize: 10, width: 10, color: row.isMajorHeading && !row.isOther ? theme.palette.primary.main : row.groupColor }}>
                            {row.isCollapsed ? '▶' : '▼'}
                        </Typography>
                    )}
                    <Typography sx={{
                        fontWeight: row.isTotal || row.isMajorHeading || row.isGroupLabel || row.isSubtotal ? 700 : 400,
                        color: row.isMajorHeading && !row.isOther ? theme.palette.primary.main : (row.isGroupLabel && row.groupColor ? row.groupColor : undefined),
                    }}>
                        {value}
                    </Typography>
                </Box>
            ),
        },
        ...buckets.map(b => ({
            id: b.id,
            label: b.label,
            type: 'currency' as const,
            size: 240,
            customHeader: () => (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{b.label}</Typography>
                        {/* Only the last bucket can be partial (see getBuckets in
                        forecast-groups.ts) - when the horizon cuts it off
                        mid-period, flag it so the total there doesn't read as a
                        genuinely low forecast for the whole month/quarter/year
                        when it's really just missing whatever falls after this date. */}
                        {b.asOf && (
                            <Tooltip
                                title={`Forecast data only goes up to ${formatDate(b.asOf)} - this period isn't complete`}
                                componentsProps={{ tooltip: { sx: { backgroundColor: '#fffefb', color: '#333333', fontSize: '14px', border: '1px solid #e9e9e9', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)' } } }}
                                arrow
                            >
                                <Box
                                    sx={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        width: 17, height: 17, borderRadius: '50%',
                                        background: '#F59E0B', flexShrink: 0, cursor: 'default',
                                    }}
                                >
                                    <Info size={12} color="#fff" strokeWidth={2.5} />
                                </Box>
                            </Tooltip>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Typography sx={{ fontSize: 11, fontStyle: 'italic' }}>Forecast</Typography>
                        <Typography sx={{ fontSize: 11, fontStyle: 'italic' }}>vs</Typography>
                        <Typography sx={{ fontSize: 11, fontStyle: 'italic' }}>Actual</Typography>
                    </Box>
                </Box>
            ),
            // Every row now carries real values (Major Heading and Sub Heading rows
            // carry their merged total/subtotal) - no more blank-cell case.
            // A small red corner triangle flags cells the applied simulation changed (either
            // the forecast or the actual value) from the un-simulated baseline (Excel "cell comment" style).
            customElement: (value: ForecastActual, row: Record<string, any>) => {
                const isAffected = (row.affectedBuckets as Set<string> | undefined)?.has(b.id);
                // Leaf rows scope to their one categoryKey; TOTAL/Subtotal rows scope to
                // every category rolled into them (categoryKeys, set in buildGroupTotalRow).
                const cellCategoryKeys: string[] = row.categoryKey ? [row.categoryKey] : (row.categoryKeys || []);
                const canOpenDetail = isAffected && cellCategoryKeys.length > 0;
                const { variance, rate, varianceValue } = formatVarianceTooltip(value?.forecast, value?.actual);
                const varianceColor = varianceValue > 0 ? '#1a7f37' : varianceValue < 0 ? '#d32f2f' : 'inherit';
                const varianceTooltip = (
                    <Box sx={{ p: 0.25 }}>
                        <Typography sx={{ fontSize: 12, color: varianceColor }}>Variance: {variance}</Typography>
                        <Typography sx={{ fontSize: 12, color: varianceColor }}>Variance Rate: {rate}</Typography>
                    </Box>
                );
                const tooltipComponentsProps = {
                    tooltip: {
                        sx: {
                            backgroundColor: '#fffefb',
                            color: '#333333',
                            border: '1px solid #e9e9e9',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        }
                    }
                };
                return (
                    <Box
                        sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center', px: 1, position: 'relative', cursor: canOpenDetail ? 'pointer' : 'default' }}
                        onClick={canOpenDetail ? () => setCellDetailTarget({ categoryKeys: cellCategoryKeys, categoryLabel: row.category, bucketId: b.id, bucketLabel: b.label }) : undefined}
                    >
                        {isAffected && (
                            <Box sx={{
                                position: 'absolute', top: -6, right: -6, width: 0, height: 0,
                                borderStyle: 'solid', borderWidth: '0 18px 18px 0',
                                borderColor: 'transparent #d32f2f transparent transparent',
                            }} />
                        )}
                        <Tooltip title={varianceTooltip} componentsProps={tooltipComponentsProps} arrow>
                            <Typography sx={{ whiteSpace: 'nowrap', textAlign: 'right', flex: 1, color: '#454545', fontWeight: row.isTotal || row.isSubtotal ? 700 : 400, cursor: 'default' }}>{formatAmount?.(value?.forecast)}</Typography>
                        </Tooltip>
                        <Tooltip title={varianceTooltip} componentsProps={tooltipComponentsProps} arrow>
                            <Typography sx={{ whiteSpace: 'nowrap', textAlign: 'right', flex: 1, fontWeight: row.isTotal || row.isSubtotal ? 700 : 600, cursor: 'default' }}>{formatAmount?.(value?.actual)}</Typography>
                        </Tooltip>
                    </Box>
                );
            },
        })),
    ];

    // Period / simulation / share controls - in the Topbar on desktop, on
    // their own wrapping row under it on phones.
    const headerControls = (mobile: boolean) => (
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" pr={mobile ? 0 : 1} flexWrap={mobile ? 'wrap' : 'nowrap'} gap={mobile ? 1 : 0}>
            <Box display="flex" alignItems="center" gap={mobile ? 1 : 2}>
                <Dropdown
                    options={[
                        { label: 'by Month', value: 'month' },
                        { label: 'by Quarter', value: 'quarter' },
                        { label: 'by Year', value: 'year' },
                    ]}
                    value={period}
                    onChange={(value) => setPeriod(value as PeriodType)}
                    width={mobile ? 120 : 150}
                    height={28}
                    isPrimary
                />
                {/* Side by side with short labels on phones, saving a row. */}
                <Box display="flex" flexDirection={mobile ? 'row' : 'column'} gap={mobile ? 0.5 : 0.25}>
                    <Button size="small" sx={{ fontSize: 10, height: 25, textWrap: 'nowrap' }} variant="outlined" onClick={() => { setSimulationsPopupCategoryId(undefined); setSimulationsPopupOpen(true); }}>
                        {appliedSimulation?.name || (mobile ? 'Simulation' : 'Select Simulation')}
                    </Button>
                    <Button size="small" sx={{ fontSize: 10, height: 25, textWrap: 'nowrap' }} variant="outlined" onClick={() => setAppliedSimulation(null)} disabled={!appliedSimulation}>
                        {mobile ? 'Clear' : 'Clear Simulation'}
                    </Button>
                </Box>
            </Box>
            <Box display="flex" alignItems="center">
                {appliedSimulation && (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 2, py: 0.75, borderRadius: 999,
                        background: hexToRgba(theme.palette.primary.main, 0.12),
                        border: `1.5px solid ${theme.palette.primary.main}`,
                    }}>
                        <Triquetra size={18} color={theme.palette.primary.main} />
                        <Box>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: theme.palette.primary.main, letterSpacing: 0.5, lineHeight: 1.2 }}>
                                SIMULATION ACTIVE
                            </Typography>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: theme.palette.primary.main, lineHeight: 1.2 }}>
                                {appliedSimulation.name}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
            <Box sx={{ width: mobile ? 'auto' : 150, display: 'flex', justifyContent: 'flex-end' }}>
                {/* Sharing is part of the Documents add-on - without it, the plan prompt. */}
                <Button
                    size="small"
                    startIcon={<Share2 size={14} />}
                    onClick={() => (hasDocumentsAddon ? setShareOpen(true) : openPlanPrompt(user?.entityId, 'documents'))}
                    sx={{ fontSize: 12, textTransform: 'none', color: 'var(--color-primary)' }}
                >
                    Share
                </Button>
            </Box>
        </Box>
    );

    return (
        <div style={{ width: '100%' }}>
           <Topbar
                isMobile={isMobile}
            >
                <Box display="flex" alignItems="center" gap={1} mr={2}>
                    {isMobile && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setSidebarOpen(true)}
                            sx={{
                                color: '#fff',
                                height: 40,
                                display: 'flex',
                                gap: 1,
                                minWidth: 40,
                                p: 1,
                                backgroundColor: 'var(--color-primary)',
                                '&:hover': { backgroundColor: 'var(--color-primary)' },
                            }}
                        >
                            <Menu size={20} color="#fff" />
                        </Button>
                    )}
                    <Typography fontWeight={isMobile ? 600 : 500 } pl={isMobile ? 0 : 3} variant="h6" sx={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                        Forecast vs Actual
                    </Typography>
                </Box>
                {!isMobile && headerControls(false)}
            </Topbar>
            {/* Phones: the period/simulation/share controls get their own wrapping
                row - squeezed into the Topbar they made the page wider than the
                screen, and the phone zoomed the whole page out. */}
            {isMobile && <Box sx={{ px: 1.25, pt: 1 }}>{headerControls(true)}</Box>}
            <ShareReport
                open={shareOpen}
                onClose={() => setShareOpen(false)}
                entityId={user?.entityId ?? null}
                defaultTitle={`Forecast vs Actual — ${periodLabels[period]}`}
                buildSnapshot={buildSnapshot}
            />
            <SimulationsPopup
                open={simulationsPopupOpen}
                onClose={() => setSimulationsPopupOpen(false)}
                onSimulationSelect={(sim) => { setAppliedSimulation(sim); setSimulationsPopupOpen(false); }}
                isReal={true}
                isRecurringProvisions={true}
                isOneTimeProvisions={true}
                appliedSimulationId={appliedSimulation?.id || null}
                initialCategoryId={simulationsPopupCategoryId}
            />
            <SimulationCellDetail
                open={!!cellDetailTarget}
                onClose={() => setCellDetailTarget(null)}
                title={cellDetailTarget ? `${cellDetailTarget.categoryLabel} — ${cellDetailTarget.bucketLabel}` : ''}
                forecastBaseline={cellDetailTransactions.forecastBaseline}
                forecastSimulated={cellDetailTransactions.forecastSimulated}
                actualBaseline={cellDetailTransactions.actualBaseline}
                actualSimulated={cellDetailTransactions.actualSimulated}
                onEditInSimulation={() => {
                    if (!cellDetailTarget) return;
                    // A TOTAL/Subtotal cell can span multiple categories - only scope the
                    // editor to one when there's exactly one candidate, otherwise fall back
                    // to the full unfiltered picker rather than guessing which one to show.
                    const [onlyKey, ...rest] = cellDetailTarget.categoryKeys;
                    setSimulationsPopupCategoryId(
                        onlyKey === undefined || rest.length > 0 ? undefined : (onlyKey === UNCATEGORIZED_KEY ? null : Number(onlyKey))
                    );
                    setCellDetailTarget(null);
                    setSimulationsPopupOpen(true);
                }}
            />
            <Box sx={{ padding: isMobile ? 0.5 : 1, width: '100%', boxSizing: 'border-box' }}>
                <DynamicTable
                    columns={buildColumns()}
                    data={groups.flatMap(g => g.rows)}
                    rowBg={(row) => {
                        // "Other" stays plain (an explicit value, not undefined - otherwise
                        // the table's own isTotal fallback re-applies a tint anyway).
                        if (row.isMajorHeading) return row.isOther ? 'var(--color-container-background)' : hexToRgba(theme.palette.primary.main, 0.16);
                        return row.groupColor ? hexToRgba(row.groupColor, row.isGroupLabel || row.isSubtotal ? 0.16 : 0.06) : undefined;
                    }}
                    headerBg={hexToRgba(theme.palette.primary.main, 0.16)}
                    headerTextColor="var(--color-default-text)"
                    headerPadding="6px 12px"
                    headerRounded={false}
                    stickyFirstColumn
                    stickyHeader
                    maxWidth='100%'
                    maxHeight="calc(100vh - 160px)"
                />
            </Box>
        </div>
    );
};

export default BudgetActual;
